import * as moment from 'moment'
import * as React from 'react'
import { connect } from 'react-redux'
import { TracepointRule } from './domain/TracepointRule'
import Link from './helper/link'
import Loading from './helper/loading'
import { ApplicationState } from './redux/ApplicationState'
import { fetchActiveTracepoints, FetchTracepointParameters, fetchTracepoints } from './redux/V2/TracepointRule/actions'
import { areTracepointRulesLoaded, getTracepointRules } from './redux/V2/TracepointRule/TracepointRuleState'
import { Box, BoxBody, BoxHeader, BoxTitle } from './ui/box'
import { Col, Row } from './ui/grid'
import VisibilityChange from './visibilityChange'
import { TracepointListItem } from './tracepoint-list-item'
import { SERVICE_ALL } from './domain/Service'

interface ReduxStateProps {
    tracepointRules: Array<TracepointRule<moment.Moment>>
    tracepointsLoaded: boolean
    service: string
    environment: string
}

interface DispatchProps {
    fetchTracepoints: (params: FetchTracepointParameters) => void
    fetchActiveTracepoints: (params: FetchTracepointParameters) => void
}

interface State {
    browserActive: boolean
}

type Props = ReduxStateProps & DispatchProps

class TracepointListComponent extends React.PureComponent<Props, State> {
    public state: State = {
        browserActive: true,
    }
    private interval
    private visibility
    private visibilityIndex

    public componentDidMount(): void {
        this.visibility = new VisibilityChange()

        const { environment, service } = this.props

        this.registerCallback({ environment, service })

        this.props.fetchTracepoints({ environment, service })

        this.setInterval({ environment, service })
    }

    public componentDidUpdate(prevProps: Readonly<Props>) {
        const { environment, service } = this.props
        if (prevProps.environment === environment && prevProps.service === service) {
            return
        }

        if (this.interval) {
            clearInterval(this.interval)
        }

        this.visibility.removeCallBack(this.visibilityIndex)

        this.registerCallback({ environment, service })

        this.setInterval({ environment, service })

        this.props.fetchTracepoints({ environment, service })
    }

    public componentWillUnmount(): void {
        if (this.interval) {
            clearInterval(this.interval)
        }

        this.visibility.removeCallBack(this.visibilityIndex)
    }

    public render() {
        if (!this.props.tracepointsLoaded) {
            return (
                <React.Fragment>
                    <Loading />
                </React.Fragment>
            )
        }

        const tracepoints = this.props.tracepointRules.sort((a, b) => b.activeUntil.unix() - a.activeUntil.unix()).slice(0, 20)

        if (tracepoints.length === 0) {
            return <React.Fragment>{this.noTracepointsYet()}</React.Fragment>
        }

        return (
            <React.Fragment>
                <Box>
                    <BoxHeader>
                        <BoxTitle>Tracepoints</BoxTitle>
                        <div className="box-tools pull-right">
                            <Link className="btn btn-default" route="XhprofProfilerBundle.AutomaticTracepointTrigger.settings">
                                <i className="fa fa-cogs" /> Configure Automatic Tracepoint Triggers
                            </Link>
                        </div>
                    </BoxHeader>
                    <BoxBody noPadding={tracepoints.length > 0}>
                        <table className="table table-condensed">
                            <tbody>
                                {tracepoints.map((tracepoint, index) => (
                                    <TracepointListItem tracepoint={tracepoint} index={index} key={tracepoint.id} />
                                ))}
                            </tbody>
                        </table>
                    </BoxBody>
                </Box>
            </React.Fragment>
        )
    }

    private noTracepointsYet = () => (
        <Box>
            <BoxHeader>
                <BoxTitle>
                    <i className="fa fa-crosshairs burning" /> No Tracepoints Created Yet!
                </BoxTitle>
            </BoxHeader>
            <BoxBody>
                {this.props.service === SERVICE_ALL ? (
                    <Row>
                        <Col widthMedium={6}>
                            <p className="tw-font-size-16">
                                If you want to gain deeper performance insights for specific parts (transactions/endpoints) in your system,
                                you can use tracepoints to collect more traces in a limited period of time.
                                <br />
                                To do so, follow these steps
                            </p>
                            <ol className="tw-font-size-18">
                                <li>
                                    click on <b>Performance</b> in the top menu
                                </li>
                                <li>
                                    select the transaction you want to boost in the list titled <b>Transactions</b>
                                </li>
                                <li>
                                    click on the button <b>Boost Traces</b>
                                </li>
                                <li>select the options you would prefer and save your tracepoint</li>
                            </ol>
                        </Col>
                    </Row>
                ) : (
                    <Row>
                        <Col widthMedium={6}>
                            <p className="tw-font-size-16">
                                There is no tracepoint data for service <strong>{this.props.service}</strong> and environment{' '}
                                <strong>{this.props.environment}</strong>. Select All services to see if there is tracepoint data available
                                for other services.
                            </p>
                        </Col>
                    </Row>
                )}
            </BoxBody>
        </Box>
    )

    private setInterval({ environment, service }) {
        let count = 0
        this.interval = setInterval(() => {
            if (!this.state.browserActive) {
                return
            }

            count++
            if (count % 6 === 0) {
                this.props.fetchTracepoints({ environment, service })
            } else {
                this.props.fetchActiveTracepoints({ environment, service })
            }
        }, 10000)
    }

    private registerCallback({ environment, service }) {
        this.visibilityIndex = this.visibility.registerCallBack(
            () => this.setState({ browserActive: false }),
            () => {
                this.props.fetchTracepoints({ environment, service })
                this.setState({ browserActive: true })
            },
        )
    }
}

export const TracepointList = connect<ReduxStateProps, DispatchProps>(
    (globalState: ApplicationState, props: Props): ReduxStateProps => {
        return {
            tracepointRules: getTracepointRules(globalState),
            tracepointsLoaded: areTracepointRulesLoaded(globalState),
            service: globalState.tidewaysContext.service,
            environment: globalState.tidewaysContext.environment,
        }
    },
    { fetchTracepoints, fetchActiveTracepoints },
)(TracepointListComponent)
